import {
  COBRANCAS_DE_EXTRA,
  type CobrancaDeExtra,
  ROTULO_DA_COBRANCA,
  ROTULO_DO_TIPO_DE_EXTRA,
  type TipoDeExtra,
  TIPOS_DE_EXTRA,
} from '@locaveri/shared'
import type { ColumnDef } from '@tanstack/react-table'
import {
  CircleCheck,
  CircleSlash,
  EllipsisVertical,
  Package,
  Pencil,
  Plus,
  Power,
  Search,
  ShieldCheck,
  UsersRound,
} from 'lucide-react'
import { type ReactElement, useMemo, useState } from 'react'
import { toast } from 'sonner'

import { Paginacao } from '@/components/data/paginacao'
import { TabelaDeDados } from '@/components/data/tabela-de-dados'
import { EmptyState } from '@/components/state/empty-state'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import type { FiltroDeAtivo } from '@/features/precos/api'
import { BadgeDeTipoDeExtra } from '@/features/precos/badges'
import {
  DialogoDeExtra,
  type Extra,
  MULTIPLICADOR_DA_COBRANCA,
  useAtualizarExtra,
  useDesativarExtra,
  useExtras,
} from '@/features/precos/dialogo-de-extra'
import { errorMessage } from '@/lib/api'
import { SEM_VALOR } from '@/lib/datas'
import { formatarMoedaDoWire } from '@/lib/dinheiro'
import { useValorAtrasado } from '@/lib/hooks'
import { usePermissao } from '@/lib/permissoes'

/**
 * Catálogo de extras.
 *
 * ---------------------------------------------------------------------------
 * A COLUNA QUE DECIDE O VALOR FINAL NÃO É A DO PREÇO
 * ---------------------------------------------------------------------------
 * "R$ 89,00" não diz nada sozinho: o mesmo número vira R$ 445 numa proteção de
 * cinco diárias (`POR_DIARIA`), R$ 89 numa entrega fora da base
 * (`POR_RESERVA`) e o que a quantidade mandar num pacote de km extra
 * (`POR_UNIDADE`). Por isso preço e cobrança dividem a MESMA célula, com o
 * multiplicador escrito por extenso embaixo do valor — não num `title`, não
 * num selo abreviado. Quem confere a tabela de extras está conferindo quanto o
 * cliente vai pagar, e a metade que falta é sempre esta.
 *
 * ---------------------------------------------------------------------------
 * POR TIPO, MAS SEM AGRUPAR A PÁGINA
 * ---------------------------------------------------------------------------
 * A lista chega na ORDEM DA VITRINE (`ordemDeExibicao`, depois nome) — é a
 * ordem que o gestor escolheu para o cliente ver, e ela é informação. Agrupar
 * por tipo aqui quebraria isso e, pior, mentiria: a paginação é do servidor, e
 * um "grupo" montado sobre vinte linhas carregadas parece completo sem ser.
 * Então o tipo é a primeira coluna, é ordenável e é o primeiro filtro — quem
 * quer ver só as proteções filtra, e aí a página inteira é daquele tipo.
 *
 * ---------------------------------------------------------------------------
 * O AMARELO APARECE UMA VEZ
 * ---------------------------------------------------------------------------
 * No CTA "Novo extra". Franquia, dependência de escala e situação são selos
 * cinza, âmbar e verde semânticos: `exigePrestador` é aviso (o extra SOME do
 * orçamento quando não há gente livre), e aviso é âmbar — nunca a cor da marca.
 */

const TAMANHO_DA_PAGINA = 20

/** Um sanduíche de zeros largo o bastante para qualquer valor que caiba num `BIGINT`. */
const LARGURA_DA_CHAVE_DE_VALOR = 20

/** Os dois estados do filtro booleano. `''` (todos) não entra na lista. */
const OPCOES_DE_ATIVO = ['true', 'false'] as const satisfies readonly FiltroDeAtivo[]

const ROTULO_DE_ATIVO: Readonly<Record<'true' | 'false', string>> = {
  true: 'Na vitrine',
  false: 'Fora da vitrine',
}

const ROTULO_DE_PRESTADOR: Readonly<Record<'true' | 'false', string>> = {
  true: 'Depende de escala',
  false: 'Não depende de escala',
}

/**
 * Chave de ORDENAÇÃO de uma coluna monetária: a string de centavos preenchida
 * com zeros à esquerda, que ordena lexicograficamente na mesma ordem do número
 * — sem passar por `Number`, que perde precisão acima de 2^53 e é proibido em
 * dinheiro (invariante nº 1).
 */
function chaveDeOrdenacaoDeValor(valor: string): string {
  return valor.padStart(LARGURA_DA_CHAVE_DE_VALOR, '0')
}

/**
 * Situação na vitrine. Verde/cinza + ícone + rótulo: fora da vitrine não é erro
 * (é uma decisão), então não é vermelho — mas também não depende só da cor.
 *
 * O texto vem de `ROTULO_DE_ATIVO`, o MESMO mapa que nomeia as opções do filtro
 * de situação. Redigitá-lo aqui é o jeito de o selo e o filtro divergirem na
 * primeira vez que alguém reescrever um dos dois: quem filtra por "Fora da
 * vitrine" tem de encontrar essa palavra, letra por letra, na coluna.
 */
function BadgeDeVitrine({ ativo }: { readonly ativo: boolean }): ReactElement {
  return ativo ? (
    <Badge variant="success">
      <CircleCheck aria-hidden="true" />
      {ROTULO_DE_ATIVO.true}
    </Badge>
  ) : (
    <Badge variant="neutral">
      <CircleSlash aria-hidden="true" />
      {ROTULO_DE_ATIVO.false}
    </Badge>
  )
}

export function ExtrasPage(): ReactElement {
  const podeCriar = usePermissao('extra.create')
  const podeEditar = usePermissao('extra.update')
  const podeDesativar = usePermissao('extra.delete')

  const [pagina, setPagina] = useState(1)
  const [buscaDigitada, setBuscaDigitada] = useState('')
  const [tipo, setTipo] = useState<TipoDeExtra | ''>('')
  const [cobranca, setCobranca] = useState<CobrancaDeExtra | ''>('')
  const [exigePrestador, setExigePrestador] = useState<FiltroDeAtivo>('')
  const [ativo, setAtivo] = useState<FiltroDeAtivo>('')

  const [dialogoAberto, setDialogoAberto] = useState(false)
  const [emEdicao, setEmEdicao] = useState<Extra | undefined>(undefined)

  const busca = useValorAtrasado(buscaDigitada)

  const filtros = useMemo(
    () => ({
      pagina,
      tamanho: TAMANHO_DA_PAGINA,
      busca,
      tipo,
      cobranca,
      exigePrestador,
      ativo,
    }),
    [pagina, busca, tipo, cobranca, exigePrestador, ativo],
  )

  const consulta = useExtras(filtros)
  const atualizar = useAtualizarExtra()
  const desativar = useDesativarExtra()

  const escrevendo = atualizar.isPending || desativar.isPending

  const temFiltro =
    busca.length > 0 || tipo !== '' || cobranca !== '' || exigePrestador !== '' || ativo !== ''

  function limparFiltros(): void {
    setBuscaDigitada('')
    setTipo('')
    setCobranca('')
    setExigePrestador('')
    setAtivo('')
    setPagina(1)
  }

  function abrirCriacao(): void {
    setEmEdicao(undefined)
    setDialogoAberto(true)
  }

  function abrirEdicao(extra: Extra): void {
    setEmEdicao(extra)
    setDialogoAberto(true)
  }

  /**
   * Tirar da vitrine NÃO apaga: as reservas que já contrataram o extra
   * continuam apontando para ele, e a Fase 4 precisa do nome para escrever o
   * contrato. Por isso não há confirmação — a ação é reversível com um clique
   * na mesma lista, e um diálogo aqui só treinaria a pessoa a confirmar sem ler.
   */
  function tirarDaVitrine(extra: Extra): void {
    desativar.mutate(extra.id, {
      onSuccess: () => {
        toast.success(`${extra.nome} saiu da vitrine`, {
          description: 'O extra some do app. Nada foi apagado — reativar é um clique.',
        })
      },
      onError: (causa: unknown) => {
        toast.error('Não foi possível tirar da vitrine', { description: errorMessage(causa) })
      },
    })
  }

  /** Reativar é um `PATCH` com `ativo: true` — a desativação não apagou nada. */
  function reativar(extra: Extra): void {
    atualizar.mutate(
      { id: extra.id, dados: { ativo: true } },
      {
        onSuccess: () => {
          toast.success(`${extra.nome} voltou à vitrine`)
        },
        onError: (causa: unknown) => {
          toast.error('Não foi possível reativar o extra', { description: errorMessage(causa) })
        },
      },
    )
  }

  const colunas = useMemo<ColumnDef<Extra>[]>(
    () => [
      {
        id: 'extra',
        header: 'Extra',
        accessorFn: (linha: Extra) => linha.nome,
        cell: ({ row }) => {
          const extra: Extra = row.original

          return (
            <div className="min-w-0 max-w-72">
              <p className="truncate font-medium text-foreground">{extra.nome}</p>
              {extra.descricao === null || extra.descricao.length === 0 ? null : (
                <p className="truncate text-xs text-fg-secondary">{extra.descricao}</p>
              )}
            </div>
          )
        },
      },
      {
        id: 'tipo',
        header: 'Tipo',
        accessorFn: (linha: Extra) => ROTULO_DO_TIPO_DE_EXTRA[linha.tipo],
        cell: ({ row }) => {
          const extra: Extra = row.original

          return (
            <div className="flex flex-col items-start gap-1">
              <BadgeDeTipoDeExtra tipo={extra.tipo} />
              {/* Franquia só existe em proteção — e é ela que distingue as três
                  proteções entre si, então mora ao lado do tipo, não perdida
                  numa coluna própria que ficaria vazia em nove linhas de dez. */}
              {extra.franquia === null ? null : (
                <span className="text-xs tabular-money text-fg-secondary">
                  Franquia {formatarMoedaDoWire(extra.franquia)}
                </span>
              )}
            </div>
          )
        },
      },
      {
        id: 'cobranca',
        header: 'Preço e cobrança',
        meta: { alinhamento: 'direita' },
        accessorFn: (linha: Extra) => chaveDeOrdenacaoDeValor(linha.valorUnitario),
        cell: ({ row }) => {
          const extra: Extra = row.original

          return (
            // `money-cell` alinha o valor à DIREITA; a cobrança acompanha. Sem
            // isto o número e a frase que o explica ficam em bordas opostas da
            // mesma célula, e a metade que decide o preço parece pertencer à
            // coluna vizinha.
            <div className="min-w-40 text-right">
              <span className="money-cell block text-foreground">
                {formatarMoedaDoWire(extra.valorUnitario)}
              </span>
              {/* A metade que decide o valor final. Ver a nota do topo. */}
              <span className="block text-xs text-fg-secondary">
                {MULTIPLICADOR_DA_COBRANCA[extra.cobranca]}
              </span>
            </div>
          )
        },
      },
      {
        id: 'limites',
        header: 'Limites',
        enableSorting: false,
        cell: ({ row }) => {
          const extra: Extra = row.original

          return (
            <div className="flex flex-col items-start gap-1">
              <span className="text-xs tabular-money text-fg-secondary">
                {extra.quantidadeMaxima === null
                  ? 'Sem teto de quantidade'
                  : `Até ${String(extra.quantidadeMaxima)} por reserva`}
              </span>
              {/* Âmbar, nunca o amarelo da marca: é aviso operacional, e o
                  rótulo diz a consequência — o extra some do orçamento. */}
              {extra.exigePrestador ? (
                <Badge
                  variant="warning"
                  title="Sem motorista ou segurança livre no período, a cotação recusa este extra"
                >
                  <UsersRound aria-hidden="true" />
                  {ROTULO_DE_PRESTADOR.true}
                </Badge>
              ) : null}
            </div>
          )
        },
      },
      {
        id: 'vitrine',
        header: 'Vitrine',
        accessorFn: (linha: Extra) => linha.ordemDeExibicao,
        cell: ({ row }) => {
          const extra: Extra = row.original

          return (
            <div className="flex flex-col items-start gap-1">
              <BadgeDeVitrine ativo={extra.ativo} />
              <span className="text-xs tabular-money text-fg-muted">
                Ordem {extra.ordemDeExibicao}
              </span>
            </div>
          )
        },
      },
      {
        id: 'acoes',
        header: '',
        enableSorting: false,
        cell: ({ row }) => {
          const extra: Extra = row.original

          // A ação disponível depende da SITUAÇÃO da linha, não só da permissão:
          // reativar só existe em extra fora da vitrine, tirar do ar só em extra
          // que está nela. Sem contar as duas coisas juntas, quem tivesse
          // `extra.delete` sem `extra.update` abriria um menu VAZIO em toda linha
          // já desativada.
          const podeReativar = podeEditar && !extra.ativo
          const podeTirarDoAr = podeDesativar && extra.ativo

          if (!podeEditar && !podeTirarDoAr) {
            return <span className="text-xs text-fg-muted">{SEM_VALOR}</span>
          }

          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Ações de ${extra.nome}`}
                  className="size-8"
                >
                  <EllipsisVertical aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>

              <DropdownMenuContent align="end" className="w-56">
                {podeEditar ? (
                  <DropdownMenuItem
                    onSelect={() => {
                      abrirEdicao(extra)
                    }}
                  >
                    <Pencil aria-hidden="true" />
                    Editar extra
                  </DropdownMenuItem>
                ) : null}

                {podeReativar ? (
                  <DropdownMenuItem
                    // Uma escrita por vez: o item continua no menu enquanto a
                    // requisição corre, e sem isto o segundo clique dispararia
                    // outra e outro toast dizendo a mesma coisa.
                    disabled={escrevendo}
                    onSelect={() => {
                      reativar(extra)
                    }}
                  >
                    <Power aria-hidden="true" />
                    Devolver à vitrine
                  </DropdownMenuItem>
                ) : null}

                {podeTirarDoAr ? (
                  <>
                    {podeEditar ? <DropdownMenuSeparator /> : null}
                    <DropdownMenuItem
                      disabled={escrevendo}
                      onSelect={() => {
                        tirarDaVitrine(extra)
                      }}
                    >
                      <CircleSlash aria-hidden="true" />
                      Tirar da vitrine
                    </DropdownMenuItem>
                  </>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          )
        },
      },
    ],
    // As funções de ação são estáveis dentro do render desta tela; o que muda o
    // conjunto de colunas são as permissões e a escrita em curso.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [podeEditar, podeDesativar, escrevendo],
  )

  const dados = consulta.data

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Extras</h1>
          <p className="max-w-prose text-sm text-fg-secondary">
            O que entra na conta além da diária. O preço unitário sozinho não diz quanto o cliente
            paga — quem decide isso é a cobrança: por diária, por reserva ou por unidade.
          </p>
        </div>

        {podeCriar ? (
          <Button onClick={abrirCriacao}>
            <Plus aria-hidden="true" />
            Novo extra
          </Button>
        ) : null}
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <div className="space-y-1.5">
          <Label htmlFor="filtro-tipo-extras">Tipo</Label>
          <Select
            id="filtro-tipo-extras"
            value={tipo}
            onChange={(evento) => {
              setPagina(1)
              const valor = evento.target.value
              setTipo(TIPOS_DE_EXTRA.find((item) => item === valor) ?? '')
            }}
          >
            <option value="">Todos os tipos</option>
            {TIPOS_DE_EXTRA.map((item) => (
              <option key={item} value={item}>
                {ROTULO_DO_TIPO_DE_EXTRA[item]}
              </option>
            ))}
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="filtro-cobranca-extras">Cobrança</Label>
          <Select
            id="filtro-cobranca-extras"
            value={cobranca}
            onChange={(evento) => {
              setPagina(1)
              const valor = evento.target.value
              setCobranca(COBRANCAS_DE_EXTRA.find((item) => item === valor) ?? '')
            }}
          >
            <option value="">Todas as formas</option>
            {COBRANCAS_DE_EXTRA.map((item) => (
              <option key={item} value={item}>
                {ROTULO_DA_COBRANCA[item]}
              </option>
            ))}
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="filtro-busca-extras">Buscar</Label>
          <div className="relative">
            <Search
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2
                text-fg-muted"
            />
            <Input
              id="filtro-busca-extras"
              type="search"
              value={buscaDigitada}
              onChange={(evento) => {
                setPagina(1)
                setBuscaDigitada(evento.target.value)
              }}
              placeholder="Nome ou descrição"
              className="pl-9"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="filtro-prestador-extras">Escala</Label>
          <Select
            id="filtro-prestador-extras"
            value={exigePrestador}
            onChange={(evento) => {
              setPagina(1)
              const valor = evento.target.value
              setExigePrestador(OPCOES_DE_ATIVO.find((item) => item === valor) ?? '')
            }}
          >
            <option value="">Tanto faz</option>
            {OPCOES_DE_ATIVO.map((item) => (
              <option key={item} value={item}>
                {ROTULO_DE_PRESTADOR[item]}
              </option>
            ))}
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="filtro-ativo-extras">Situação</Label>
          <Select
            id="filtro-ativo-extras"
            value={ativo}
            onChange={(evento) => {
              setPagina(1)
              const valor = evento.target.value
              setAtivo(OPCOES_DE_ATIVO.find((item) => item === valor) ?? '')
            }}
          >
            <option value="">Todas</option>
            {OPCOES_DE_ATIVO.map((item) => (
              <option key={item} value={item}>
                {ROTULO_DE_ATIVO[item]}
              </option>
            ))}
          </Select>
        </div>
      </div>

      <TabelaDeDados
        colunas={colunas}
        dados={dados?.itens ?? []}
        carregando={consulta.isPending}
        erro={consulta.isError ? consulta.error : undefined}
        aoTentarNovamente={() => {
          void consulta.refetch()
        }}
        legenda="Catálogo de extras da locação"
        estadoVazio={
          <EmptyState
            icone={temFiltro ? Package : ShieldCheck}
            titulo={temFiltro ? 'Nenhum extra com esses filtros' : 'Nenhum extra cadastrado'}
            descricao={
              temFiltro
                ? 'Ajuste o tipo, a cobrança ou a busca para ver outros resultados.'
                : 'Comece pelas proteções: é o extra que mais entra na conta e o único tipo que aceita franquia.'
            }
            acao={
              temFiltro ? (
                <Button variant="secondary" onClick={limparFiltros}>
                  Limpar filtros
                </Button>
              ) : podeCriar ? (
                <Button onClick={abrirCriacao}>
                  <Plus aria-hidden="true" />
                  Novo extra
                </Button>
              ) : undefined
            }
          />
        }
      />

      <Paginacao
        pagina={dados?.pagina ?? pagina}
        tamanho={dados?.tamanho ?? TAMANHO_DA_PAGINA}
        total={dados?.total ?? 0}
        aoMudarDePagina={setPagina}
        desabilitado={consulta.isFetching}
      />

      <DialogoDeExtra
        aberto={dialogoAberto}
        extra={emEdicao}
        aoFechar={() => {
          setDialogoAberto(false)
        }}
      />
    </div>
  )
}
